// File: src/components/social/CommentInput/CommentInput.tsx
// Purpose: Social interaction component (comments, likes, shares)

import React, { useState } from "react";
import styled from "styled-components";
import { Send } from "lucide-react";

interface CommentInputProps {
  placeholder?: string;
  onSend: (comment: string) => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.surface};
`;

const TextArea = styled.textarea`
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SendButton = styled.button`
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

export const CommentInput: React.FC<CommentInputProps> = ({ placeholder = "Write a comment...", onSend }) => {
  const [comment, setComment] = useState("");

  const handleSend = () => {
    if (comment.trim()) {
      onSend(comment);
      setComment(""); // Clear input after sending
    }
  };

  return (
    <Container>
      <TextArea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={placeholder}
        rows={1}
      />
      <SendButton onClick={handleSend} aria-label="Send comment">
        <Send size={20} />
      </SendButton>
    </Container>
  );
};